import { QuestionInterface } from '@api/assessmentQuestionnaireApiHandler';
import { KeyConfigurations } from '@enums/KeyConfigurations';
import { CircularProgress } from '@mui/material';
import { MotionText, Slider } from '@shared/atoms';
import { SystemButton } from '@shared/molecules';
import React, { useEffect, useRef, useMemo } from 'react';
import { FeelingTodaySkeleton } from './LoadingSkeletons';
import styles from './index.module.scss';

interface CheckInContentProps {
  questionnairesLoading: boolean | undefined;
  questionsLoading: boolean;
  sortedQuestions: QuestionInterface[];
  ratings: Record<number, number>;
  onRatingChange: (questionId: number, value: number, questionLabel: string) => void;
  onSubmit: () => void;
  onClose: () => void;
  announcement: string;
  isSubmitting?: boolean;
  sliderChangeDetected?: boolean;
}

export const CheckInContent: React.FC<CheckInContentProps> = ({
  questionnairesLoading,
  questionsLoading,
  sortedQuestions,
  ratings,
  onRatingChange,
  onSubmit,
  onClose,
  announcement,
  isSubmitting,
  sliderChangeDetected,
}) => {
  const formRef = useRef<HTMLFormElement>(null);

  const handleSliderChange =
    (questionId: number, questionLabel: string) => (event: Event, newValue: number | number[]) => {
      const valueAsNumber = newValue as number;
      onRatingChange(questionId, valueAsNumber, questionLabel);
    };

  useEffect(() => {
    if (formRef.current && sortedQuestions.length > 0) {
      const firstSlider = formRef.current.querySelector(`[id^="slider-"]`);

      if (firstSlider) {
        (firstSlider as HTMLElement).focus();
      }
    }
  }, [sortedQuestions.length]);

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === KeyConfigurations.ARROW_DOWN || event.key === KeyConfigurations.TAB) {
      const nextSlider = formRef.current?.querySelector(`#slider-${index + 1}`);

      if (nextSlider && event.key === KeyConfigurations.ARROW_DOWN) {
        event.preventDefault();
        (nextSlider as HTMLElement).focus();
      }
    } else if (event.key === KeyConfigurations.ARROW_UP) {
      const prevSlider = formRef.current?.querySelector(`#slider-${index - 1}`);

      if (prevSlider) {
        event.preventDefault();
        (prevSlider as HTMLElement).focus();
      }
    }
  };

  // Check if all questions have been answered (all have non-zero values)
  const allQuestionsAnswered = useMemo(() => {
    const DEFAULT = 0;

    return sortedQuestions.every(
      (question) => ratings[question.id] !== undefined && ratings[question.id] !== DEFAULT,
    );
  }, [sortedQuestions, ratings]);

  return (
    <div className={styles.container} role="region" aria-label="Daily Check-in Form">
      {questionnairesLoading || questionsLoading ? (
        <FeelingTodaySkeleton />
      ) : (
        <form className={styles.card} ref={formRef} aria-live="polite">
          <MotionText
            id="check-in-title"
            className={styles.title}
            as="h3"
            text="How are you feeling today?"
          />

          <div aria-live="assertive" className="visually-hidden" aria-atomic="true">
            {announcement}
          </div>

          {sortedQuestions.map((question, index) => {
            const color = '#0072b0';

            const options =
              question.questionnairesV2QuestionResponseScales
                .questionnairesV2QuestionResponseScaleOptions;
            const minValue = Math.min(...options.map((opt) => opt.score));
            const maxValue = Math.max(...options.map((opt) => opt.score));

            const minOption = options.find((opt) => opt.score === minValue);
            const maxOption = options.find((opt) => opt.score === maxValue);

            const minLabel = minOption?.optionText || minValue.toString();
            const maxLabel = maxOption?.optionText || maxValue.toString();

            const sliderId = `slider-${index}`;
            const questionId = `question-${question.id}`;
            const currentValue = ratings[question.id] || 0; // Default to 0
            const hasBeenAnswered =
              ratings[question.id] !== undefined && ratings[question.id] !== 0;

            const displayLabel = question.label || question.questionText;

            return (
              <div
                key={question.id}
                className={styles.category}
                role="group"
                aria-labelledby={questionId}
              >
                <div className={styles.categoryHeader}>
                  <div className={styles.categoryInfo}>
                    <MotionText
                      id={questionId}
                      className={styles.categoryTitle}
                      as="h4"
                      text={displayLabel}
                      style={{ color }}
                    />

                    {/* <MotionText
                      id={`${questionId}-desc`}
                      className={styles.categoryDescription}
                      as="text"
                      text={question?.questionText || question?.description}
                    /> */}
                  </div>
                  {/* Only show rating badge if the question has been answered (non-zero value) */}
                  {hasBeenAnswered && (
                    <div
                      aria-live="polite"
                      aria-atomic="true"
                      className={styles.ratingBadge}
                      style={{ backgroundColor: color }}
                    >
                      {currentValue}
                    </div>
                  )}
                </div>
                <div className={styles.slider}>
                  <Slider
                    id={sliderId}
                    sliderValue={currentValue}
                    defaultValue={0} // Set default to 0
                    handleSliderChange={handleSliderChange(question.id, displayLabel)}
                    marks
                    minValue={minValue}
                    maxValue={maxValue}
                    colorCode="#019df2"
                    backgroundColor={color}
                    valueLabel="off"
                    aria-labelledby={questionId}
                    aria-describedby={`${questionId}-desc range-label-${question.id}`}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    tabIndex={0}
                    role="slider"
                    aria-valuenow={currentValue}
                    aria-valuemin={minValue}
                    aria-valuemax={maxValue}
                    aria-orientation="horizontal"
                  />
                </div>

                <div className={styles.rangeLabels} id={`range-label-${question.id}`}>
                  <MotionText id={minLabel} as="p" text={minLabel} />
                  <MotionText id={maxLabel} as="p" text={maxLabel} />
                </div>
              </div>
            );
          })}

          <div className={styles.buttonContainer}>
            <SystemButton
              onClick={onSubmit}
              fullWidthButton
              disabled={isSubmitting || !allQuestionsAnswered} // Enable only when all questions answered
              loading={isSubmitting}
            >
              Continue
            </SystemButton>
          </div>
        </form>
      )}
    </div>
  );
};

CheckInContent.defaultProps = {
  isSubmitting: false,
  sliderChangeDetected: true,
};
